import type {Content} from '@prismicio/client'
import cn from 'classnames'
import {memo, useCallback} from 'react'

type Tour = Content.ProductTourSectionSliceDefaultPrimaryToursItem

interface TourSelectorProps {
  tours: Tour[]
  activeTour: number
  onTourSelect: (index: number) => void
}

export const TourSelector = memo<TourSelectorProps>(({tours, activeTour, onTourSelect}) => {
  const handleTourClick = useCallback(
    (index: number) => {
      if (index !== activeTour) {
        onTourSelect(index)
      }
    },
    [activeTour, onTourSelect],
  )

  return (
    <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4">
      {tours.map((tour, index) => {
        const isActive = index === activeTour
        const tourName = tour.Title || `Tour ${index + 1}`

        return (
          <div
            key={`product-tour-${index}`}
            className={cn('relative w-full rounded-[4px] p-4 transition-colors duration-300', {
              'cursor-pointer': !isActive,
              'bg-ui-green-115': isActive,
              'bg-moss-white': !isActive,
            })}
            onClick={() => handleTourClick(index)}
            role="button"
            tabIndex={isActive ? -1 : 0}
            aria-selected={isActive}
            aria-label={`${tourName}: ${tour.description}`}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isActive) {
                e.preventDefault()
                handleTourClick(index)
              }
            }}
          >
            <div
              className={cn('flex flex-col gap-2', {
                'pointer-events-none': isActive,
              })}
            >
              {tour.tag && (
                <span
                  className={cn('text-detail transition-all duration-300', {
                    'text-neon-brand-550': isActive,
                    'text-moss-grey-300': !isActive,
                  })}
                >
                  {tour.tag}
                </span>
              )}
              <h2 className="text-tg-body-l text-moss-black">{tourName}</h2>
              <p className="text-tg-body-m text-moss-grey-400">{tour.description}</p>
            </div>
            <div
              className={cn(
                'pointer-events-none absolute inset-0 rounded-[4px] border transition-colors duration-300',
                {
                  'border-green-default': isActive,
                  'border-moss-grey-150': !isActive,
                },
              )}
            />
          </div>
        )
      })}
    </div>
  )
})
